import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, ArrowUpRight, Feather, Hammer, Leaf, Sparkles } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import { api, whatsappLink } from "@/lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?auto=format&fit=crop&w=1600&q=85";

const CRAFT_IMG_1 =
  "https://images.pexels.com/photos/29848182/pexels-photo-29848182.jpeg?auto=compress&cs=tinysrgb&w=1200";
const CRAFT_IMG_2 =
  "https://images.pexels.com/photos/37877827/pexels-photo-37877827.jpeg?auto=compress&cs=tinysrgb&w=1200";

const LIFESTYLE_IMG =
  "https://images.unsplash.com/photo-1705326701287-346fc37a2c86?auto=format&fit=crop&w=1600&q=85";

const TESTIMONIALS = [
  {
    quote:
      "The Terra Bloom rug feels like it has always belonged in our home. You can see the care in every knot.",
    author: "Anaïs V., Interior Designer, Paris",
  },
  {
    quote:
      "We turned our daughter's crayon drawing into a woven carpet. It made her cry — in the best way.",
    author: "Rohan & Meera, Bengaluru",
  },
  {
    quote:
      "The frame arrived with a handwritten note from the craftsman. Unheard of at this level of finish.",
    author: "Studio Nord, Copenhagen",
  },
];

const MARQUEE = [
  "Vogue Living",
  "Architectural Digest India",
  "Elle Decor",
  "Kinfolk",
  "The Wall Street Journal",
  "Cereal Magazine",
];

export default function Home() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api
      .get("/products")
      .then((r) => setProducts(r.data.products || []))
      .catch(() => {});
  }, []);

  const featured = products.slice(0, 4);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 pt-10 md:pt-16 pb-24 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="overline fade-up">Handcrafted in India — since 1978</div>
            <h1 className="fade-up delay-1 mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] leading-[0.95] tracking-[-0.02em] text-[#1B3B36] font-light">
              Woven slowly,
              <br />
              <em className="italic font-light text-[#84968C]">
                on purpose.
              </em>
            </h1>
            <p className="fade-up delay-2 mt-8 text-[17px] leading-relaxed text-[#4A4A4A] font-light max-w-xl">
              A quiet atelier of weavers, carpenters and colourists making
              carpets, rugs, photo frames and children's baskets — one considered
              piece at a time.
            </p>
            <div className="fade-up delay-3 mt-10 flex flex-wrap gap-4">
              <Link
                to="/collections"
                data-testid="hero-cta-collections"
                className="inline-flex items-center gap-3 bg-[#1B3B36] text-[#F9F8F6] px-8 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-[#132A26] transition-colors"
              >
                Explore the Collection <ArrowRight size={14} />
              </Link>
              <Link
                to="/designer"
                data-testid="hero-cta-designer"
                className="inline-flex items-center gap-3 border border-[#1B3B36] text-[#1B3B36] px-8 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-[#1B3B36] hover:text-[#F9F8F6] transition-colors"
              >
                Design Your Own
              </Link>
            </div>
            <div className="fade-up delay-4 mt-14 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { n: "46", l: "Master artisans" },
                { n: "9,200", l: "Knots per sq. ft." },
                { n: "72 hrs", l: "For 1 sq. ft. of silk" },
              ].map((s) => (
                <div key={s.l}>
                  <div className="font-serif text-3xl text-[#1B3B36]">{s.n}</div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#84968C] mt-1">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 order-1 lg:order-2 relative">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={HERO_IMG}
                alt="Iryaa Homes hero"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1B3B36]/25 via-transparent to-transparent" />
            </div>
            <div className="hidden lg:block absolute -bottom-10 -left-10 bg-[#F9F8F6] p-6 max-w-xs shadow-[0_20px_60px_-20px_rgba(27,59,54,0.35)] border border-[#D8D4CC]">
              <div className="overline">Piece No. 0417</div>
              <div className="font-serif text-xl text-[#1B3B36] mt-2 leading-tight">
                "Ivory Meadow" — hand-knotted over 96 days in Bhadohi.
              </div>
            </div>
          </div>
        </div>

        {/* press marquee */}
        <div className="border-y border-[#D8D4CC] py-6 overflow-hidden bg-[#EFECE5]">
          <div className="flex gap-16 animate-marquee whitespace-nowrap">
            {[...MARQUEE, ...MARQUEE].map((m, i) => (
              <div
                key={i}
                className="font-serif text-2xl italic text-[#84968C] px-4"
              >
                {m}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-14">
          <div className="md:col-span-7">
            <div className="overline">Four crafts, one atelier</div>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl font-light text-[#1B3B36] tracking-tight leading-[1.05]">
              Objects that hold the memory of hands.
            </h2>
          </div>
          <div className="md:col-span-5 md:pl-8">
            <p className="text-[15px] leading-relaxed text-[#4A4A4A] font-light">
              From silk-blend heirloom carpets to seagrass baskets for a
              little one's room — each collection is small on purpose, so we
              can be exacting.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-4">
          {[
            {
              slug: "carpets",
              name: "Carpets",
              img: "https://images.unsplash.com/photo-1615529182904-14819c35db37?auto=format&fit=crop&w=900&q=80",
            },
            {
              slug: "rugs",
              name: "Rugs",
              img: "https://images.unsplash.com/photo-1615873968403-89e068629265?auto=format&fit=crop&w=900&q=80",
            },
            {
              slug: "photo-frames",
              name: "Photo Frames",
              img: "https://images.unsplash.com/photo-1582053628662-c65b0e0544e9?auto=format&fit=crop&w=900&q=80",
            },
            {
              slug: "kids-baskets",
              name: "Kids Baskets",
              img: "https://images.unsplash.com/photo-1610384104075-e05c8cf200c3?auto=format&fit=crop&w=900&q=80",
            },
          ].map((c, i) => (
            <Link
              key={c.slug}
              to={`/collections?cat=${c.slug}`}
              data-testid={`category-tile-${c.slug}`}
              className={`group relative block overflow-hidden aspect-[3/4] hover-view ${
                i % 2 === 1 ? "md:translate-y-8" : ""
              }`}
            >
              <img
                src={c.img}
                alt={c.name}
                className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1B3B36]/60 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex justify-between items-end text-[#F9F8F6]">
                <div>
                  <div className="overline text-[#F9F8F6]/70">0{i + 1}</div>
                  <div className="font-serif text-2xl mt-1">{c.name}</div>
                </div>
                <ArrowUpRight
                  size={20}
                  className="transition-transform group-hover:rotate-45"
                />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* EDITORIAL — CRAFTSMANSHIP */}
      <section className="bg-[#1B3B36] text-[#F9F8F6] grain">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="overline text-[#84968C]">The Iryaa Way</div>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight">
              A slower way to <em className="italic">make</em> — and to&nbsp;live.
            </h2>
            <p className="mt-8 text-[15px] leading-relaxed text-[#F9F8F6]/80 max-w-md">
              Nothing about our pieces is rushed. Yarn is dyed by hand in copper
              pots, wood is aged for eighteen months before it meets a chisel,
              and every carpet is signed by the weaver who finished it.
            </p>

            <div className="mt-12 grid grid-cols-2 gap-8">
              {[
                { icon: Leaf, t: "Natural fibres", d: "Wool, silk, cotton, jute, seagrass — traceable to the field." },
                { icon: Hammer, t: "Small hands", d: "A team of 46 artisans, never a factory line." },
                { icon: Feather, t: "Light footprint", d: "Vegetable dyes, solar dyeing pits, plastic-free packing." },
                { icon: Sparkles, t: "Signed & numbered", d: "Every piece carries its maker's mark, forever." },
              ].map((f) => (
                <div key={f.t}>
                  <f.icon size={18} className="text-[#84968C]" />
                  <div className="font-serif text-xl mt-3">{f.t}</div>
                  <div className="text-[13px] text-[#F9F8F6]/75 mt-2 leading-relaxed font-light">
                    {f.d}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 gap-4 md:gap-6">
            <img
              src={CRAFT_IMG_1}
              alt="Artisan at work"
              className="w-full h-full object-cover aspect-[3/4]"
            />
            <img
              src={CRAFT_IMG_2}
              alt="Artisan detailing"
              className="w-full h-full object-cover aspect-[3/4] mt-12"
            />
          </div>
        </div>
      </section>

      {/* FEATURED PIECES */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <div className="overline">This season</div>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl font-light text-[#1B3B36] leading-[1.05]">
              Pieces we are quietly proud of.
            </h2>
          </div>
          <Link
            to="/collections"
            data-testid="featured-view-all"
            className="text-[12px] uppercase tracking-[0.22em] link-underline text-[#1B3B36]"
          >
            View entire collection →
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {featured.map((p) => (
            <ProductCard key={p.id} product={p} testIdPrefix="home-featured" />
          ))}
        </div>
      </section>

      {/* CUSTOM CTA BANNER */}
      <section
        className="relative overflow-hidden"
        style={{ backgroundColor: "#EFECE5" }}
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="overline">The Custom Studio</div>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl font-light text-[#1B3B36] leading-[1.05] tracking-tight">
              Bring us a memory. We'll weave it.
            </h2>
            <p className="mt-8 text-[15px] leading-relaxed text-[#4A4A4A] max-w-lg font-light">
              Upload any reference — a childhood photograph, a favourite
              painting, a colour that feels like home. Our design studio, guided
              by AI and finished by hand, will translate it into a woven-ready
              artwork you can approve before we begin.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/designer"
                data-testid="custom-cta-open"
                className="inline-flex items-center gap-3 bg-[#1B3B36] text-[#F9F8F6] px-8 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-[#132A26] transition-colors"
              >
                Open the Studio <ArrowRight size={14} />
              </Link>
              <a
                href={whatsappLink(
                  "Hello Iryaa, I have a custom design idea in mind. Can we speak?"
                )}
                target="_blank"
                rel="noreferrer"
                data-testid="custom-cta-whatsapp"
                className="inline-flex items-center gap-3 border border-[#1B3B36] text-[#1B3B36] px-8 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-[#1B3B36] hover:text-[#F9F8F6] transition-colors"
              >
                Speak to a designer
              </a>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="grid grid-cols-2 gap-4">
              <img
                src={LIFESTYLE_IMG}
                alt="Lifestyle"
                className="w-full h-full object-cover aspect-[4/5]"
              />
              <img
                src="https://images.unsplash.com/photo-1724582586529-62622e50c0b3?auto=format&fit=crop&w=900&q=80"
                alt="Lifestyle detail"
                className="w-full h-full object-cover aspect-[4/5] mt-12"
              />
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <div className="overline">In their homes, in their words</div>
        <h2 className="mt-4 font-serif text-4xl md:text-5xl font-light text-[#1B3B36] leading-[1.05] max-w-3xl">
          What people whisper about our work.
        </h2>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-10">
          {TESTIMONIALS.map((t, i) => (
            <figure key={i} className="border-t border-[#1B3B36] pt-6">
              <div className="overline text-[#84968C]">0{i + 1}</div>
              <blockquote className="mt-4 font-serif text-2xl leading-snug text-[#1B3B36]">
                "{t.quote}"
              </blockquote>
              <figcaption className="mt-6 text-[12px] uppercase tracking-[0.18em] text-[#84968C]">
                — {t.author}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>
    </div>
  );
}
